<template>
   <ModalWrapper :open="open" @close="onClose" center fixedHeight>
      <template #header>
         <h3>Assign to Class</h3>
         <p class="serif grey">
            You can add and remove this activity from one of your classes, or
            you can mark the activity as complete.
         </p>
      </template>
      <template #body>
         <div v-click-away="onFocusAway" class="search-wrapper">
            <FormGenerator
               id="assign-class"
               :schema="schema"
               onUpdate
               @updated="handleClassSearch"
               @focus="focused = true"
            />
            <div
               v-if="focused"
               class="search-results active-classes"
               data-lenis-prevent
            >
               <AssignDropdownList
                  :results="filteredSearchResults"
                  label="Active"
                  @onAssign="onAssign"
                  @creatClass="creatClass"
                  :targetId="targetId"
                  :searchQuery="searchQuery"
                  :assignedClasses
               />
               <!-- <AssignDropdownList :results="searchResults.archived" /> -->
            </div>
         </div>
         <AssignedList
            v-if="assignedClasses.length"
            :classes="assignedClasses"
            label="Assigned to:"
            @unassign="unassign"
         />
      </template>
      <template #footer>
         <Btn
            classes="button save-button green"
            text="Done"
            @click="onClose"
         ></Btn>
      </template>
   </ModalWrapper>
</template>

<script setup lang="ts">
import { clickAway as vClickAway } from "~/directives/clickAway";
import type { Classroom } from "~/types/classroom";

type TargetType = "teachables" | "media" | "units";
type MediaType = "wistia" | "audio" | "image";

const route = useRoute();
const Fuse = useNuxtApp().$fuse as new (
   list: unknown[],
   options?: unknown,
) => { search(query: string): Array<{ item: Classroom.Simplified }> };
const classesStore = useClassesStore();
const { activeClasses, archivedClasses } = storeToRefs(classesStore);

const emit = defineEmits<{
   (e: "assignClass", id: number): void;
   (e: "close"): void;
}>();
// PROPS ========================
const props = defineProps<{
   open: boolean;
   targetType: TargetType;
   mediaType?: MediaType;
   targetId?: number | null;
}>();
// FETCH ========================
await useAsyncData("assign-class-modal-classes", () => {
   if (!activeClasses.value?.length) {
      return classesStore.queryClasses();
   } else {
      return false;
   }
});

// REFS ========================
const schema = reactive({
   fields: [
      {
         label: "Search for a class",
         name: "search",
         placeholder: "Class name",
         as: "input",
         type: "search",
         autocomplete: "one-time-code",
      },
   ],
});
// Search ========================
const searching = ref<boolean>(false);
const focused = ref<boolean>(false);
const searchResults = reactive<{
   active: Classroom.Simplified[];
   archived: Classroom.Simplified[];
}>({
   active: [],
   archived: [],
});
const fuseOptions = {
   includeScore: true,
   keys: ["title"],
};
type FuseInstance = InstanceType<typeof Fuse>;
let fuseActive: FuseInstance | null = null;
let fuseArchived: FuseInstance | null = null;

// COMPUTED ========================
const assignedClasses = computed(() => {
   if (!activeClasses.value && !archivedClasses.value) {
      return [];
   }
   // Combine active + archived in one array
   // const allClasses = [...activeClasses.value, ...archivedClasses.value];
   // NOTE: we are not using all classes because archived classes error on removal. We can this n later with a feature to now allow removal
   // Filter the array of classes:
   // Keep only the classes that have at least one matching module
   const mediaKey = props.mediaType == "wistia" ? "videos" : props.mediaType;
   // This is getting a bit complicated but essentially we are checking for each entity type and then matching against the slug or id depending on the type. Media usees ID
   return activeClasses.value?.filter((classItem) => {
      const childrenToFilter =
         props.targetType == "units"
            ? classItem.unit_sequences
            : props.targetType == "media" && mediaKey
            ? (classItem.media as unknown as Record<string, Array<{ id?: number; slug?: string }>>)[mediaKey]
            : classItem.modules;
      return (childrenToFilter as Array<{ id?: number; slug?: string }> | undefined)?.some((mod) => {
         return props.mediaType
            ? mod.id === props.targetId
            : mod?.slug === route.params?.slug;
      });
   });
});

const filteredSearchResults = computed(() => {
   // return searchResults.active.filter(
   //    (searchResult) =>
   //       !assignedClasses.value.some(
   //          (assignedClass) => assignedClass.id === searchResult.id
   //       )
   // );
   return searchResults.active;
});

const searchQuery = ref<string>("");
// METHODS ========================
const handleClassSearch = ({ search }: { search: string }) => {
   searching.value = search.length ? true : false;
   searchQuery.value = search;
   searchResults.active =
      fuseActive?.search(search).map((result) => result.item) ?? [];
   searchResults.archived =
      fuseArchived?.search(search).map((result) => result.item) ?? [];
};

const onAssign = async (classId: number) => {
   await classesStore.assignToClass(
      props.targetType,
      (props.targetId ?? String(route.params.slug ?? "")) as string | number,
      classId,
      "POST",
      props.mediaType === "image" ? null : (props.mediaType ?? null) as "video" | "audio" | null,
   );
};
const unassign = async (classId: number) => {
   await classesStore.assignToClass(
      props.targetType,
      (props.targetId ?? String(route.params.slug ?? "")) as string | number,
      classId,
      "DELETE",
      props.mediaType === "image" ? null : (props.mediaType ?? null) as "video" | "audio" | null,
   );
};
const creatClass = async (name: string) => {
   const data = await classesStore.createClass(name as unknown as FormData);
   data?.id && onAssign(data.id);
   searching.value = false;
   searchQuery.value = "";
   focused.value = false;
};
const onFocusAway = () => {
   focused.value = false;
};

const onClose = () => {
   focused.value = false;
   searching.value = false;
   searchQuery.value = "";
   emit("close");
};

// WATCH ========================
watchEffect(() => {
   if (focused.value && !searching.value) {
      searchResults.active = activeClasses.value ?? [];
      searchResults.archived = archivedClasses.value ?? [];
   }
});

// LIFECYCLE ========================
onMounted(() => {
   fuseActive = new Fuse(activeClasses.value ?? [], fuseOptions);
   fuseArchived = new Fuse(archivedClasses.value ?? [], fuseOptions);
});
</script>

<style lang="scss">
.modal-body {
   padding: pad(2) 0 pad(4);
   @include flex(column, stretch, stretch);
   row-gap: pad(4);
   div.search-wrapper {
      position: relative;
      z-index: 5;
      div.form-wrapper {
         position: relative;
         div.search {
            width: 100%;
         }
      }
      div.search-results {
         top: calc(100% - 3px);
         width: 100%;
         max-height: 225px;
         overflow: scroll;
         border-radius: 0 0 pad(0.5) pad(0.5);
         @include flex(column, stretch, stretch);
         row-gap: pad(2);
         z-index: 0;
      }
   }
}
.modal-footer {
   @include flex(row, flex-end, space-between);
}
</style>
